{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://raw.githubusercontent.com/jwildfire/safety.viz/main/src/data/schema/time-to-event.json",
  "title": "safety.viz time-to-event data contract",
  "description": "Two datasets: event-level records (one row per event, e.g. an ADAE projection) and population records (one row per participant, e.g. an ADSL projection carrying the follow-up-end study day). The module composes the endpoint from the events passing the active multiselect filters (TTE-FILT-001), takes each participant's first qualifying event by onset day (TTE-DERIV-001), censors event-free participants at the population follow-up day (TTE-DERIV-002), and computes the Kaplan-Meier product-limit estimator, Greenwood variance and log-log pointwise 95% bounds from the derived observations. The derivation rule is fixed and stated in the clinical guide; records that cannot be used are counted and exported rather than dropped silently (TTE-DATA-002). Column names are supplied by the settings mapping.",
  "type": "object",
  "required": ["events", "population", "settings"],
  "properties": {
    "events": {
      "type": "array",
      "items": { "type": "object" },
      "requiredSettings": ["id_col", "event_day_col"],
      "description": "d3.csv()-style event records; every row carries the participant id and onset-day columns named in settings, plus any descriptor columns the event filters use (body system, preferred term, seriousness, severity, ...). Rows with a missing, non-numeric or non-positive day, or a participant absent from the population data, are excluded with a counted, exportable reason (TTE-DATA-002)."
    },
    "population": {
      "type": "array",
      "minItems": 1,
      "items": { "type": "object" },
      "requiredSettings": ["id_col", "fu_day_col"],
      "description": "d3.csv()-style population records, one row per participant: the analysis denominator. Every row carries the participant id and follow-up-end day columns named in settings; the group column is optional (one pooled curve when absent, TTE-DATA-004). Later duplicate rows for a participant are excluded with a counted reason (TTE-DATA-003)."
    },
    "settings": {
      "type": "object",
      "description": "Column mappings and rendering options; merged onto the module's DEFAULT_SETTINGS, so only overrides need to be supplied.",
      "properties": {
        "id_col": {
          "type": "string",
          "default": "USUBJID",
          "description": "Participant identifier column, required in both datasets, and the key the participantsSelected event carries (TTE-CFG-001)."
        },
        "group_col": {
          "type": ["string", "null"],
          "default": "ARM",
          "description": "Population curve-grouping column (treatment arm). Optional: when the population has no such column, one pooled curve is drawn (TTE-DATA-004)."
        },
        "fu_day_col": {
          "type": "string",
          "default": "EOSDY",
          "description": "Population follow-up-end study day (day 1 = first dose): the censoring time for participants with no qualifying event (TTE-DERIV-002); required in population data. Event-free participants without a usable value are excluded with a counted reason."
        },
        "censor_desc_col": {
          "type": ["string", "null"],
          "default": "EOSSTT",
          "description": "Optional population censoring description (e.g. end-of-study status), shown in censor-mark tooltips."
        },
        "event_day_col": {
          "type": "string",
          "default": "ASTDY",
          "description": "Event onset study day (day 1 = first dose); required in event data. Missing, non-numeric or non-positive days exclude the row with a counted, exportable reason (TTE-DATA-002)."
        },
        "event_desc_col": {
          "type": ["string", "null"],
          "default": "AEDECOD",
          "description": "Optional event description (e.g. the preferred term), shown in event tooltips for the qualifying event."
        },
        "event_filters": {
          "type": "array",
          "default": ["AEBODSYS", "AEDECOD", "AESER", "AESEV"],
          "items": {
            "oneOf": [
              { "type": "string" },
              {
                "type": "object",
                "required": ["value_col"],
                "properties": {
                  "value_col": { "type": "string" },
                  "label": { "type": "string" }
                }
              }
            ]
          },
          "description": "Multiselect filter controls over the event dataset — the endpoint composer (TTE-FILT-001): column names or { value_col, label } specs. Which columns get a filter is configurable; which values qualify is the reviewer's live choice, never hard-coded. A filter whose column is absent from the events is dropped with a console warning."
        },
        "endpoint_label": {
          "type": "string",
          "default": "Time to first qualifying event",
          "description": "Display name for the composed endpoint, used in the notes (TTE-FILT-003)."
        },
        "filters": {
          "type": "array",
          "default": [],
          "items": {
            "oneOf": [
              { "type": "string" },
              {
                "type": "object",
                "required": ["value_col"],
                "properties": {
                  "value_col": { "type": "string" },
                  "label": { "type": "string" },
                  "start": {
                    "type": ["string", "number", "boolean", "array", "null"],
                    "default": null,
                    "description": "The value the filter opens on; an array of values for a `multiple` filter. 0 and false are real values, not an absent start. A value the data lacks is dropped with a console warning."
                  },
                  "all": {
                    "type": "boolean",
                    "default": true,
                    "description": "Whether the filter offers an \"All\" option. false removes it, and the filter then always holds one value: the first, unless `start` names another."
                  },
                  "multiple": {
                    "type": "boolean",
                    "default": false,
                    "description": "Render a checkbox multiselect instead of a dropdown; the rows shown are those matching any selected value."
                  }
                }
              }
            ]
          },
          "description": "Filter controls over the population rows: column names or { value_col, label, start, all, multiple } specs — `start` is the value the filter opens on, `all` (default true) is whether an \"All\" option is offered, and `multiple` (default false) makes it a checkbox multiselect. A filter whose column is absent from the population is dropped with a console warning."
        },
        "direction": {
          "type": "string",
          "enum": ["incidence", "survival"],
          "default": "incidence",
          "description": "Display orientation: cumulative incidence (1 - KM, rising - the safety convention and the FDA ST&F figures' orientation, D2) or survival (falling). The y-axis names the estimator either way (TTE-CURV-004)."
        },
        "ci": {
          "type": "boolean",
          "default": true,
          "description": "Whether to draw the pointwise 95% confidence band (Greenwood variance, log-log transform, matching survival::survfit conf.type='log-log'; D3). The band is pointwise, not simultaneous, and is labelled as such (TTE-CURV-003)."
        },
        "time_unit": {
          "type": "string",
          "default": "day",
          "description": "Axis label unit; display only - the module never rescales times."
        },
        "width": {
          "type": "string",
          "default": "100%",
          "description": "Widget width, carried for the R widget bindings; the shell always spans its container."
        },
        "height": {
          "type": "number",
          "default": 560,
          "description": "Chart-area height in pixels, including the in-canvas at-risk / cumulative-events table."
        }
      }
    }
  }
}
